import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { parseAccessoryData } from 'helpers/Accessories';
import { IAccessory, ICharging, IExterior } from 'interfaces/buildAndPrice';
import {
  IInitialRAQVehicle,
  IInitialVehicle,
  IReduxAccessory,
  IReduxColor,
  IVehicle,
} from 'interfaces/vehicle';
import { RootState } from 'redux/store';
import ECConfigurationManager from 'rulesEngine/rulesEngine';
import { parseInitialData } from 'rulesEngine/rulesParser';

export interface IInitialData {
  vehicleData: IInitialVehicle;
  colorCombinations: IExterior[];
  accessories: IAccessory[];
  charging: ICharging[];
  model: {
    modelId: string;
    destinationCharge: string;
    msrp: string;
    msrpDisclaimer: string;
  };
  tab: string;
}
let rulesEngine = new ECConfigurationManager();

const initialConflict = {
  opCode: '',
  selectedAccessory: {} as IReduxAccessory,
  addList: [],
  removeList: [],
  excludedPackages: [],
  isOpen: false,
};

const emptyAccessories = [
  {
    opCode: '',
    name: 'empty',
    enName: 'empty',
    price: 0,
    image: {
      alternativeText: '',
      sources: { small: '' },
    },
    isPackage: false,
    hasIRIAssets: false,
    iriZOrder: 0,
    categoryName: '',
    includedBy: [],
    showInIri: false,
  },
];
export const emptyColor = {
  code: 'empty',
  internalCode: 'empty',
  colorIdentifier: 'empty',
  name: '',
  price: 0,
  image: {
    alternativeText: '',
    sources: { small: '' },
  },
};

const initialState: IVehicle = {
  drivetrain: '',
  accessories: [...emptyAccessories],
  charging: null,
  engine: '',
  exteriorColor: { ...emptyColor },
  interiorColor: { ...emptyColor },
  defaultInteriorCode: '',
  msrpDisclaimer: '',
  modelID: '',
  bodyStyle: '',
  modelSeries: '',
  standaloneModelSeries: '',
  paragraphModelSeries: '',
  modelYear: '',
  transmission: '',
  msrpPrice: 0,
  accessoriesPrice: 0,
  destHandlingPrice: 0,
  totalPrice: 0,
  conflict: initialConflict,
  iriNotification: false,
  hasBeenModified: false,
  prepaidMaintenance: false,
  flyoutSupplyMessage: '',
  packageName: '',
  tab: '',
};
export const vehicleSlice = createSlice({
  name: 'vehicle',
  initialState,
  reducers: {
    setInitialData: (state, action: PayloadAction<IInitialData>) => {
      let rulesEngineState;
      const parsedInitialData = parseInitialData(action.payload);
      const { vehicleData, tab } = action.payload;
      const {
        modelYear,
        modelSeries,
        standaloneModelSeries,
        paragraphModelSeries,
        msrpDisclaimer,
        modelID,
        exteriorColor,
        interiorColor,
        defaultInteriorCode,
        drivetrain,
        engine,
        transmission,
        accessories,
        charging,
        prepaidMaintenance,
        flyoutSupplyMessage,
        packageName,
      } = vehicleData;
      if (!state.totalPrice) {
        const interiorCode =
          interiorColor.internalCode === 'empty'
            ? ''
            : interiorColor.internalCode;
        rulesEngine.AddModels(parsedInitialData);
        rulesEngine.ProcessModelColorRules(
          modelID,
          exteriorColor.internalCode,
          interiorCode
        );

        rulesEngineState = rulesEngine.GetModel(modelID);
        state.destHandlingPrice = parseInt(
          rulesEngineState.DestinationHandling,
          10
        );
        state.msrpPrice = Math.ceil(parseFloat(rulesEngineState.MSRP));
        state.totalPrice = Math.ceil(parseFloat(rulesEngineState.TotalPrice));
      }

      if (
        state.accessories.length === 1 &&
        state.accessories[0].name === 'empty'
      ) {
        let removedAccessories = [] as string[];
        accessories.forEach(({ opCode }) => {
          if (removedAccessories.indexOf(opCode) === -1) {
            const accValidation = rulesEngine.ProcessModelOptionRules(
              modelID,
              opCode,
              false
            );
            if (accValidation.PassedValidation) {
              rulesEngine.ProcessModelOptionRules(modelID, opCode, true);
            } else {
              rulesEngine.ProcessModelOptionRules(modelID, opCode, true);

              removedAccessories = [
                ...removedAccessories,
                ...accValidation.IncludedOptions.map(
                  (include) => include.OpCode
                ),
                ...accValidation.RequiredOptions.map(
                  (require) => require.OpCode
                ),
              ];
            }
          }
        });

        rulesEngineState = rulesEngine.GetModel(modelID);
        state.totalPrice = Math.ceil(parseFloat(rulesEngineState.TotalPrice));
        state.accessories = accessories;
        state.charging = charging;
      }

      state.modelYear = modelYear;
      state.modelSeries = modelSeries;
      state.standaloneModelSeries = standaloneModelSeries;
      state.paragraphModelSeries = paragraphModelSeries;
      state.msrpDisclaimer = msrpDisclaimer;
      state.modelID = modelID;
      state.packageName = packageName;
      state.flyoutSupplyMessage = flyoutSupplyMessage;
      state.prepaidMaintenance = prepaidMaintenance;
      state.exteriorColor = exteriorColor;
      state.interiorColor = interiorColor;
      state.defaultInteriorCode = defaultInteriorCode;
      state.drivetrain = drivetrain;
      state.engine = engine;
      state.transmission = transmission;
      state.accessoriesPrice = rulesEngineState
        ? rulesEngineState.TotalPrice -
          state.exteriorColor.price -
          rulesEngineState.MSRP
        : 0;
      state.tab = tab;

      const config = getSavingConfig(state);
      localStorage.setItem(config.key, JSON.stringify(config.value));
      localStorage.setItem('last-build-data', JSON.stringify(config.value));
    },
    resetData: (state) => {
      rulesEngine = new ECConfigurationManager();
      state.modelYear = '';
      state.modelSeries = '';
      state.standaloneModelSeries = '';
      state.paragraphModelSeries = '';
      state.modelID = '';
      state.exteriorColor = emptyColor;
      state.interiorColor = emptyColor;
      state.defaultInteriorCode = '';
      state.accessories = emptyAccessories;
      state.charging = null;
      state.drivetrain = '';
      state.engine = '';
      state.transmission = '';
      state.destHandlingPrice = 0;
      state.msrpPrice = 0;
      state.totalPrice = 0;
      state.accessoriesPrice = 0;
      state.hasBeenModified = false;
      state.tab = '';
    },
    setTabValue: (state, action: PayloadAction<string>) => {
      state.tab = action.payload;

      const config = getSavingConfig(state);
      localStorage.setItem(config.key, JSON.stringify(config.value));
      localStorage.setItem('last-build-data', JSON.stringify(config.value));
    },
    setExteriorColor: (
      state,
      action: PayloadAction<{
        exterior: IReduxColor;
        defaultInteriorCode: string;
      }>
    ) => {
      // Rules engine needs exterior and interior codes to work correctly
      const newInteriorCode =
        state.interiorColor.internalCode === 'empty'
          ? action.payload.defaultInteriorCode
          : state.interiorColor.internalCode;

      const testedValidation = rulesEngine.ProcessModelColorRules(
        state.modelID,
        action.payload.exterior.internalCode,
        newInteriorCode
      );

      console.log(
        `Exterior Color Rules passed validation: ${testedValidation.PassedValidation}`
      );
      const { TotalPrice } = rulesEngine.GetModel(state.modelID);

      state.exteriorColor = action.payload.exterior;
      state.defaultInteriorCode = newInteriorCode;
      state.totalPrice = Math.ceil(parseFloat(TotalPrice));
      state.accessoriesPrice =
        state.totalPrice - state.exteriorColor.price - state.msrpPrice;
      state.hasBeenModified = true;

      const config = getSavingConfig(state);
      localStorage.setItem(config.key, JSON.stringify(config.value));
      localStorage.setItem('last-build-data', JSON.stringify(config.value));
    },
    setInteriorColor: (
      state,
      action: PayloadAction<{
        interior: IReduxColor;
        defaultInteriorCode?: string;
      }>
    ) => {
      const interiorCode =
        action.payload.interior.internalCode === 'empty'
          ? action.payload.defaultInteriorCode || ''
          : action.payload.interior.internalCode;
      const testedValidation = rulesEngine.ProcessModelColorRules(
        state.modelID,
        state.exteriorColor.internalCode,
        interiorCode
      );

      console.log(
        `Interior Color Rules passed validation: ${testedValidation.PassedValidation}`
      );
      const { TotalPrice } = rulesEngine.GetModel(state.modelID);

      state.interiorColor = action.payload.interior;
      state.defaultInteriorCode = interiorCode;
      state.totalPrice = Math.ceil(parseFloat(TotalPrice));
      state.accessoriesPrice =
        state.totalPrice - state.exteriorColor.price - state.msrpPrice;
      action.payload.interior.hasBeenModified !== false &&
        (state.hasBeenModified = true);

      const config = getSavingConfig(state);
      localStorage.setItem(config.key, JSON.stringify(config.value));
      localStorage.setItem('last-build-data', JSON.stringify(config.value));
    },
    setVehicleData: (state, action: PayloadAction<IInitialRAQVehicle>) => {
      const { modelYear, modelSeries, modelID } = action.payload;
      state.modelYear = modelYear || '';
      state.modelSeries = modelSeries || '';
      state.modelID = modelID || '';
    },
    toggleCharging: (state, action: PayloadAction<ICharging>) => {
      if (state.charging?.opCode === action.payload.opCode) {
        state.charging = null;
      } else {
        state.charging = action.payload;
      }
    },
    toggleAccessory: (state, action: PayloadAction<IAccessory>) => {
      const { modelID } = state;
      const { opCode } = action.payload;
      const selectedAccessory = parseAccessoryData(action.payload);
      const currentAccessories = state.accessories || [];
      const isSelected = currentAccessories.find(
        (accessory) => accessory.opCode === opCode
      );

      const {
        PassedValidation,
        ExcludedOptions,
        IncludedOptions,
        RequiredOptions,
      } = rulesEngine.ProcessModelOptionRules(modelID, opCode, false);
      console.log('passed', PassedValidation);
      console.log('excluded', ExcludedOptions);
      console.log('Included', IncludedOptions);
      console.log('Required', RequiredOptions);
      console.log('RulesEngine', rulesEngine.GetModel(modelID));

      // Remove hidden included accessories for FE View
      const includeListingNoHidden = IncludedOptions.filter(
        (includedItem) => !includedItem.isHidden
      );
      // Remove hidden required accessories for FE View
      const requireListingNoHidden = RequiredOptions.filter(
        (requireItem) => !requireItem.isHidden
      );
      //Remove hidden accessories for the FE view
      const excludedListingNoHidden = ExcludedOptions.filter((excludedItem) => {
        return !excludedItem.isHidden;
      });

      // passedValidation = true ||
      // passedValidation = false & noExcludedOptions & is not selected
      // Just apply selection
      if (
        PassedValidation ||
        (!PassedValidation && ExcludedOptions.length === 0 && !isSelected)
      ) {
        // Apply accessories to the rules engine
        rulesEngine.ProcessModelOptionRules(modelID, opCode, true);
        const { TotalPrice } = rulesEngine.GetModel(modelID);
        // Parse the list of include accessories to be added
        const includeListing = includeListingNoHidden.map(parseAccessoryData);
        const includeHasIriList = includeListing.map((opt) => opt.hasIRIAssets);

        // Parse the list of require accessories to be added
        const requireListing = requireListingNoHidden.map(parseAccessoryData);
        const requireHasIriList = requireListing.map((opt) => opt.hasIRIAssets);
        if (!isSelected) {
          //Create new array with all the accessories to be added and the current accessories
          state.accessories = [
            ...currentAccessories,
            ...includeListing,
            ...requireListing,
            selectedAccessory,
          ];
          // create a list with the hasIriAsset prop of all items added
          const hasIriAccessories = [
            ...includeHasIriList,
            ...requireHasIriList,
          ];

          state.iriNotification =
            hasIriAccessories.indexOf(false) !== -1 ||
            (hasIriAccessories.length === 0 && !selectedAccessory.hasIRIAssets);
        } else {
          // Remove the accessory selected
          state.accessories = currentAccessories.filter(
            (accOpCode) => accOpCode.opCode !== opCode
          );
        }
        state.totalPrice = Math.ceil(parseFloat(TotalPrice));
        state.accessoriesPrice =
          state.totalPrice - state.exteriorColor.price - state.msrpPrice;
      } else {
        // if excluded items are all hidden force selection without conflict modal
        if (
          !includeListingNoHidden.length &&
          !requireListingNoHidden.length &&
          !excludedListingNoHidden.length
        ) {
          // Apply accessories to the rules engine
          rulesEngine.ProcessModelOptionRules(modelID, opCode, true);
          const { TotalPrice } = rulesEngine.GetModel(modelID);
          if (!isSelected) {
            //Create new array with all the accessories to be added and the current accessories
            state.accessories = [...currentAccessories, selectedAccessory];
            state.iriNotification = !selectedAccessory.hasIRIAssets;
          } else {
            // Remove the accessory selected
            state.accessories = currentAccessories.filter(
              (accOpCode) => accOpCode.opCode !== opCode
            );
          }
          // Save the new price in the state
          state.totalPrice = Math.ceil(parseFloat(TotalPrice));
          state.accessoriesPrice =
            state.totalPrice - state.exteriorColor.price - state.msrpPrice;
        } else {
          //Extract excluded packages or accessories with required accessories
          const excludedPackages = ExcludedOptions.filter((excludedItem) => {
            return (
              excludedItem.IncludeList.length > 0 ||
              excludedItem.RequiredList.length > 0
            );
          });
          // Set conflict state to display the conflict modal
          state.conflict = {
            opCode: opCode, // accessory selected
            selectedAccessory: selectedAccessory,
            addList: [
              ...includeListingNoHidden.map(parseAccessoryData),
              ...requireListingNoHidden.map(parseAccessoryData),
              ...(isSelected ? [] : [selectedAccessory]), // accessory selected added if not previous selected
            ],
            removeList: [
              ...(isSelected ? [selectedAccessory] : []), // accessory selected removed if previous selected
              ...excludedListingNoHidden.map(parseAccessoryData),
            ],
            excludedPackages: [...excludedPackages.map(parseAccessoryData)],
            isOpen: true, // opens the conflict modal
          };
        }
      }
      const config = getSavingConfig(state);
      localStorage.setItem(config.key, JSON.stringify(config.value));
      localStorage.setItem('last-build-data', JSON.stringify(config.value));

      state.hasBeenModified = true;
    },
    removeAccessory: (state, action: PayloadAction<IReduxAccessory>) => {
      const { modelID, accessories } = state;
      const { opCode } = action.payload;
      const currentAccessories = accessories || [];
      const accValidation = rulesEngine.ProcessModelOptionRules(
        modelID,
        opCode,
        false
      );
      if (accValidation.PassedValidation) {
        rulesEngine.ProcessModelOptionRules(
          state.modelID,
          action.payload.opCode,
          true
        );
        const { TotalPrice } = rulesEngine.GetModel(modelID);
        // Remove the accessory selected
        state.accessories = currentAccessories.filter(
          (accOpCode) => accOpCode.opCode !== opCode
        );

        // Save the new price in the state
        state.totalPrice = Math.ceil(parseFloat(TotalPrice));
        state.accessoriesPrice =
          state.totalPrice - state.exteriorColor.price - state.msrpPrice;

        const config = getSavingConfig(state);
        localStorage.setItem(config.key, JSON.stringify(config.value));
        localStorage.setItem('last-build-data', JSON.stringify(config.value));
      } else {
        //Remove hidden accessories for the FE view
        const Listing = accValidation.ExcludedOptions.filter((excludeItem) => {
          return !excludeItem.isHidden;
        });
        // Set conflict state to display the conflict modal
        state.conflict = {
          opCode: opCode, // accessory selected
          selectedAccessory: action.payload,
          addList: [],
          removeList: [
            action.payload, // accessory selected removed if previous selected
            ...Listing.map(parseAccessoryData),
          ],
          excludedPackages: [],
          isOpen: true, // opens the conflict modal
        };
      }
    },
    applyConflict: (state) => {
      const { modelID, conflict, accessories } = state;
      const {
        opCode,
        addList,
        removeList,
        excludedPackages,
        selectedAccessory,
      } = conflict; // Conflict state saved before modal opened
      const isSelected = accessories.find(
        (accessory) => accessory.opCode === opCode
      );
      let newAddList = [...addList];
      let newRemoveList = [...removeList];
      let currentAccessories = [...accessories];
      // Line 404-467 Patch for excluded packages fix for rules engine
      // Manual remove any excluded package founded
      if (newAddList.length > 0) {
        excludedPackages.forEach((excludedItem: any) => {
          if (
            accessories.findIndex(
              (accessory) => accessory.opCode === excludedItem.opCode
            ) !== -1
          ) {
            const rulesEngineState = rulesEngine.ProcessModelOptionRules(
              modelID,
              excludedItem.opCode,
              false
            );
            const reExcludedNoHiddenItems =
              rulesEngineState.ExcludedOptions.filter((excludedItem) => {
                return !excludedItem.isHidden;
              }).map(parseAccessoryData);

            if (reExcludedNoHiddenItems.length) {
              currentAccessories = currentAccessories?.filter((accessory) => {
                //Remove accessories on the current selected accessories
                return (
                  reExcludedNoHiddenItems.findIndex(
                    (removeAcc) => removeAcc.opCode === accessory.opCode
                  ) === -1 && accessory.opCode !== excludedItem.opCode
                );
              });
              rulesEngine.ProcessModelOptionRules(
                modelID,
                excludedItem.opCode,
                true
              );
            }
          }
        });
        // Recheck rules engine after excluded packages removed
        if (excludedPackages.length) {
          const { IncludedOptions, RequiredOptions, ExcludedOptions } =
            rulesEngine.ProcessModelOptionRules(modelID, opCode, false);

          const includeListingNoHidden = IncludedOptions.filter(
            (includedItem) => !includedItem.isHidden
          );
          // Remove hidden required accessories for FE View
          const requireListingNoHidden = RequiredOptions.filter(
            (requireItem) => !requireItem.isHidden
          );

          //Remove hidden accessories for the FE view
          const excludedListingNoHidden = ExcludedOptions.filter(
            (excludedItem) => {
              return !excludedItem.isHidden;
            }
          );
          newAddList = [
            ...includeListingNoHidden.map(parseAccessoryData),
            ...requireListingNoHidden.map(parseAccessoryData),
            ...(isSelected ? [] : [selectedAccessory]), // accessory selected added if not previous selected
          ];
          newRemoveList = [
            ...(isSelected ? [selectedAccessory] : []), // accessory selected removed if previous selected
            ...excludedListingNoHidden.map(parseAccessoryData),
          ];
        }
      }

      rulesEngine.ProcessModelOptionRules(modelID, opCode, true); // Apply saved accessories to the rules engine
      const rulesEngineState = rulesEngine.GetModel(modelID); // Get rules engine current state
      const addHasIriListing = addList.map((acc) => acc.hasIRIAssets);

      // validaton to preserve type on accessories
      // should never be null
      if (accessories !== null) {
        currentAccessories = currentAccessories?.filter((accessory) => {
          //Remove accessories on tthe current selected accessories
          return (
            newRemoveList.findIndex(
              (removeAcc) => removeAcc.opCode === accessory.opCode
            ) === -1
          );
        });
        state.accessories = [...currentAccessories, ...newAddList]; //Adds new accessories
        state.iriNotification = addHasIriListing.indexOf(false) !== -1;

        state.totalPrice = Math.ceil(parseFloat(rulesEngineState.TotalPrice)); // Save price in the redux state
        state.accessoriesPrice =
          state.totalPrice - state.exteriorColor.price - state.msrpPrice;
        state.conflict = initialConflict; // reset conflict state

        const config = getSavingConfig(state);
        localStorage.setItem(config.key, JSON.stringify(config.value));
        localStorage.setItem('last-build-data', JSON.stringify(config.value));
      }
    },
    cleanConflict: (state) => {
      state.conflict = initialConflict; // reset conflict state
    },
    hideIriNotification: (state) => {
      state.iriNotification = false;
    },
  },
});

export const {
  setInitialData,
  resetData,
  setTabValue,
  setExteriorColor,
  setInteriorColor,
  toggleAccessory,
  toggleCharging,
  removeAccessory,
  cleanConflict,
  applyConflict,
  hideIriNotification,
  setVehicleData,
} = vehicleSlice.actions;

const getSavingConfig = (state: IVehicle) => ({
  key: `${state.modelSeries}-${state.modelYear}`,
  value: {
    modelId: state.modelID,
    modelYear: state.modelYear,
    modelSeries: state.paragraphModelSeries,
    package: state.packageName,
    exterior: state.exteriorColor.internalCode,
    interior: state.defaultInteriorCode,
    engine: state.engine,
    drivetrain: state.drivetrain,
    transmission: state.transmission,
    accessories: state.accessories.map((acc) => acc.opCode),
    intAccessories: state.accessories
      .filter(
        (acc) =>
          acc.categoryName?.toLowerCase() === 'interior' && !acc.isPackage
      )
      .map((item) => item.opCode),
    extAccessories: state.accessories
      .filter(
        (acc) =>
          acc.categoryName?.toLowerCase() === 'exterior' && !acc.isPackage
      )
      .map((item) => item.opCode),
    totalPrice: state.totalPrice,
    tab: state.tab,
    timestamp: Date.now(),
  },
});
export const selectVehicleData = (state: RootState) => state.vehicle;
// Data used by the exterior page
export const getExteriorData = (state: RootState) => {
  // filter accessories only for exterior and accessory have IRI assets
  const parsedAccessories = state.vehicle.accessories.filter((acc) => {
    return (
      (acc.categoryName?.toLocaleLowerCase() === 'exterior' &&
        acc.hasIRIAssets) ||
      acc.showInIri
    );
  });
  return {
    modelId: state.vehicle.modelID,
    exteriorCode: state.vehicle.exteriorColor.internalCode,
    accessoryOpcodeQuery: parsedAccessories.length
      ? `$O:${parsedAccessories
          .sort((a, b) => a.iriZOrder - b.iriZOrder)
          .map((acc: IReduxAccessory) => acc.opCode)
          .join(',')}`
      : '',
  };
};

// Data used by the interior page
export const getInteriorData = (state: RootState) => {
  // filter accessories only for interior and have accessory have IRI assets
  const parsedAccessories = state.vehicle.accessories.filter((acc) => {
    return (
      (acc.categoryName?.toLocaleLowerCase() === 'interior' &&
        acc.hasIRIAssets) ||
      acc.showInIri
    );
  });
  return {
    modelId: state.vehicle.modelID,
    exteriorCode: state.vehicle.exteriorColor.internalCode,
    interiorColor: state.vehicle.interiorColor,
    accessoryOpcodeQuery: parsedAccessories.length
      ? `$O:${parsedAccessories
          .sort((a, b) => a.iriZOrder - b.iriZOrder)
          .map((acc: IReduxAccessory) => acc.opCode)
          .join(',')}`
      : '',
  };
};

// Data used by the flyout and summary page
export const getSummaryData = (state: RootState) => {
  return {
    modelId: state.vehicle.modelID,
    modelName: state.vehicle.modelSeries,
    modelYear: state.vehicle.modelYear,
    modelPackage: state.vehicle.packageName,
    prepaidMaintenance: state.vehicle.prepaidMaintenance,
    flyoutSupplyMessage: state.vehicle.flyoutSupplyMessage,
    startingMsrp: state.vehicle.msrpPrice,
    msrpDisclaimer: state.vehicle.msrpDisclaimer,
    destHandlingPrice: state.vehicle.destHandlingPrice,
    totalPrice: state.vehicle.totalPrice,
    selectedExterior: state.vehicle.exteriorColor,
    selectedInterior: state.vehicle.interiorColor,
    chargingPackage: state.vehicle.charging,
    accessoriesNoPackages: state.vehicle.accessories.filter(
      (acc: IReduxAccessory) => !acc.isPackage
    ),
    accessoriesPrice: state.vehicle.accessoriesPrice,
    conflictState: state.vehicle.conflict,
    transmission: state.vehicle.transmission,
    engine: state.vehicle.engine,
    drivetrain: state.vehicle.drivetrain,
  };
};
export const getSummaryTekionData = (state: RootState) => {
  const parsedEAccessories = state.vehicle.accessories.filter((acc) => {
    return (
      acc.categoryName?.toLocaleLowerCase() === 'exterior' && acc.hasIRIAssets
    );
  });
  const parsedIAccessories = state.vehicle.accessories.filter((acc) => {
    return (
      acc.categoryName?.toLocaleLowerCase() === 'interior' && acc.hasIRIAssets
    );
  });
  return {
    modelId: state.vehicle.modelID,
    modelName: state.vehicle.standaloneModelSeries,
    modelYear: state.vehicle.modelYear,
    selectedExterior: state.vehicle.exteriorColor,
    selectedInterior: state.vehicle.interiorColor,
    chargingOpcode: state.vehicle.charging?.opCode || '',
    eAccessoryOpcodeQuery: parsedEAccessories.length
      ? `$O:${parsedEAccessories
          .sort((a, b) => a.iriZOrder - b.iriZOrder)
          .map((acc: IReduxAccessory) => acc.opCode)
          .join(',')}`
      : '',
    iAccessoryOpcodeQuery: parsedIAccessories.length
      ? `$O:${parsedIAccessories
          .sort((a, b) => a.iriZOrder - b.iriZOrder)
          .map((acc: IReduxAccessory) => acc.opCode)
          .join(',')}`
      : '',
  };
};

// Data used by the charging page
export const getCurrentCharging = (state: RootState) => {
  return state.vehicle.charging;
};

export const isCurrentChargingNull = (state: RootState) => {
  return state.vehicle.charging === null;
};

// Data used by the accessories page
export const getCurrentAccessories = (state: RootState) => {
  return [...state.vehicle.accessories];
};

// Data used by the summary page
export const getSummaryAccessories = (state: RootState) => {
  let includedToRemove: any[] = [];
  let parsedSummaryAcc = [...state.vehicle.accessories];
  const includedNameByPackage: any = {};

  state.vehicle.accessories.forEach((acc) => {
    acc.includedBy?.forEach((pkgOpCode) => {
      if (
        state.vehicle.accessories.findIndex(
          (acc) => acc.opCode === pkgOpCode
        ) !== -1
      ) {
        if (includedNameByPackage[pkgOpCode] === undefined) {
          includedNameByPackage[pkgOpCode] = [acc.name];
          includedToRemove = [...includedToRemove, acc.opCode];
        } else {
          includedNameByPackage[pkgOpCode] = [
            ...includedNameByPackage[pkgOpCode],
            acc.name,
          ];
          includedToRemove = [...includedToRemove, acc.opCode];
        }
      }
    });
  });

  for (const pkg in includedNameByPackage) {
    const pkgFindedIndex: number = state.vehicle.accessories.findIndex(
      (acc) => acc.opCode === pkg
    );
    if (pkgFindedIndex !== -1) {
      parsedSummaryAcc[pkgFindedIndex] = {
        ...parsedSummaryAcc[pkgFindedIndex],
        includedAcc: includedNameByPackage[pkg],
      };
    }
  }

  return parsedSummaryAcc.filter(
    (acc) => includedToRemove.indexOf(acc.opCode) === -1
  );
};
// Data used to display the conflict modal
export const getConflictState = (state: RootState) => state.vehicle.conflict;

export const getRAQData = (state: RootState) => {
  const data = {
    modelId: state.vehicle.modelID.toUpperCase(),
    modelSeries: state.vehicle.modelSeries.toUpperCase(),
    modelYear: state.vehicle.modelYear,
    exteriorColor: state.vehicle.exteriorColor,
    interiorColor: state.vehicle.interiorColor,
    accessories: state.vehicle.accessories,
    powertrain: state.vehicle.engine,
    drivetrain: state.vehicle.drivetrain,
    package: '',
    msrp: state.vehicle.msrpPrice,
    totalAsConfigured:
      state.vehicle.totalPrice + state.vehicle.destHandlingPrice,
  };
  return data;
};
export default vehicleSlice.reducer;
